import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";

/**
 * UK postcode autocomplete using postcodes.io (no API key).
 * Props: value, onChange (string), onSelectPlace ({ postcode, admin_district, latitude, longitude })
 */
export default function PostcodeInput({ value, onChange, onSelectPlace, placeholder = "Postcode e.g. EC2A 4NE", className = "", testId }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 2) { setSuggestions([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(q)}/autocomplete`);
        const j = await res.json();
        if (!cancelled) setSuggestions(j.result || []);
      } catch { if (!cancelled) setSuggestions([]); }
      finally { if (!cancelled) setLoading(false); }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [value]);

  useEffect(() => {
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", off);
    return () => document.removeEventListener("click", off);
  }, []);

  const pick = async (pc) => {
    onChange(pc);
    setOpen(false);
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}`);
      const j = await res.json();
      if (j.result && onSelectPlace) {
        onSelectPlace({
          postcode: j.result.postcode,
          admin_district: j.result.admin_district,
          latitude: j.result.latitude,
          longitude: j.result.longitude,
        });
      }
    } catch { /* ignore */ }
  };

  return (
    <div ref={ref} className="relative">
      <Input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        data-testid={testId}
        className={className}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-border shadow-lg max-h-64 overflow-auto">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => pick(s)}
              className="w-full text-left px-4 py-2 text-sm hover:bg-sage-light font-mono"
            >
              {s}
            </button>
          ))}
          {loading && <div className="px-4 py-2 text-xs text-muted-foreground overline">Searching…</div>}
        </div>
      )}
    </div>
  );
}
